import { useCallback, useEffect, useState } from 'react';
import { Card, Modal, FormField, TextInput, PageHeader, StatusBadge } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { superApi, type McpTokenRow, type McpTokenCreated } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

/**
 * חיבור Claude Code (MCP): הנפקה וניהול של טוקני mcp: לסופר-אדמין.
 * הטוקן מוצג פעם אחת עם פקודת `claude mcp add` מוכנה להדבקה.
 */

const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleDateString('he-IL') : '—');

const mono: React.CSSProperties = {
  padding: 'var(--pad-card)', background: 'var(--surface-2)', borderRadius: 'var(--r-2)',
  fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)', wordBreak: 'break-all',
};

function CopyBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <strong style={{ fontSize: 'var(--t-sm)' }}>{label}</strong>
        <button
          type="button"
          className="btn ghost"
          onClick={() => {
            void navigator.clipboard?.writeText(value);
            toast('הועתק', 'ok');
          }}
        >
          <Icon name="copy" size={14} /> העתק
        </button>
      </div>
      <div dir="ltr" style={mono}>{value}</div>
    </div>
  );
}

export function McpSettings() {
  const [tokens, setTokens] = useState<McpTokenRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<McpTokenCreated | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<McpTokenRow | null>(null);

  const [name, setName] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setTokens(await superApi.mcpTokens());
    } catch {
      toast('טעינת הטוקנים נכשלה', 'danger');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      const result = await superApi.createMcpToken({
        name: name.trim() || undefined,
        read_only: readOnly,
      });
      setCreated(result);
      setCreateOpen(false);
      setName('');
      setReadOnly(false);
      void load();
    } catch {
      toast('יצירת הטוקן נכשלה', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (t: McpTokenRow) => {
    try {
      await superApi.revokeMcpToken(t.id);
      toast('הטוקן בוטל', 'ok');
      setRevokeTarget(null);
      void load();
    } catch {
      toast('ביטול הטוקן נכשל', 'danger');
    }
  };

  return (
    <div className="stack" style={{ gap: 'var(--gap-page)' }}>
      <PageHeader
        title="חיבור Claude (MCP)"
        subtitle="טוקנים שמחברים את Claude Code או Claude Desktop לניהול המערכת — סוכנים קוליים, מרכזיות, תורים, מספרים, webhooks ואוטומציות"
        actions={
          <button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={14} /> טוקן חדש
          </button>
        }
      />

      <Card title="איך מתחברים?">
        <p style={{ margin: '0 0 10px', fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
          צרו טוקן חדש (הכפתור למעלה) — כל פרטי החיבור יוצגו פעם אחת בלבד. בחרו את הדרך שמתאימה לכם:
        </p>
        <div className="stack" style={{ gap: 12 }}>
          <div>
            <strong style={{ fontSize: 'var(--t-sm)' }}>Claude Code (טרמינל)</strong>
            <ol style={{ margin: '4px 0 0', paddingInlineStart: 18, fontSize: 'var(--t-sm)', lineHeight: 1.8 }}>
              <li>הדביקו את פקודת <code dir="ltr">claude mcp add</code> בטרמינל.</li>
              <li>הריצו <code dir="ltr">/mcp</code> כדי לוודא שהשרת מחובר.</li>
            </ol>
          </div>
          <div>
            <strong style={{ fontSize: 'var(--t-sm)' }}>Claude Desktop (אפליקציה)</strong>
            <ol style={{ margin: '4px 0 0', paddingInlineStart: 18, fontSize: 'var(--t-sm)', lineHeight: 1.8 }}>
              <li>העתיקו את תצורת ה-Desktop לקובץ <code dir="ltr">claude_desktop_config.json</code> (הנתיב מוצג ליד התצורה).</li>
              <li>סגרו ופתחו מחדש את Claude Desktop — השרת יופיע בתפריט הכלים.</li>
            </ol>
            <p style={{ margin: '6px 0 0', fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
              דורש Node.js מותקן: Claude Desktop מריץ שרתי MCP מקומית, ולכן החיבור עובר דרך הגשר <code dir="ltr">mcp-remote</code> שמותקן אוטומטית ב-npx.
            </p>
          </div>
        </div>
      </Card>

      <Card title={`טוקנים פעילים (${tokens.length})`}>
        {loading ? (
          <p style={{ margin: 0 }}>טוען…</p>
        ) : tokens.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--text-2)' }}>אין טוקנים עדיין — צרו אחד כדי לחבר את Claude Code.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'right', fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
                <th style={{ padding: '6px 4px' }}>שם</th>
                <th style={{ padding: '6px 4px' }}>הרשאה</th>
                <th style={{ padding: '6px 4px' }}>נוצר</th>
                <th style={{ padding: '6px 4px' }}>שימוש אחרון</th>
                <th style={{ padding: '6px 4px' }}>פג תוקף</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 4px' }}>{t.name}</td>
                  <td style={{ padding: '8px 4px' }}>
                    <StatusBadge tone={t.read_only ? 'neutral' : 'ok'} dot size="sm">
                      {t.read_only ? 'קריאה בלבד' : 'מלא'}
                    </StatusBadge>
                  </td>
                  <td style={{ padding: '8px 4px' }}>{fmtDate(t.created_at)}</td>
                  <td style={{ padding: '8px 4px' }}>{fmtDate(t.last_used_at)}</td>
                  <td style={{ padding: '8px 4px' }}>{fmtDate(t.expires_at)}</td>
                  <td style={{ padding: '8px 4px', textAlign: 'left' }}>
                    <button type="button" className="btn danger" onClick={() => setRevokeTarget(t)}>
                      בטל
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {createOpen && (
        <Modal
          isOpen
          onClose={() => setCreateOpen(false)}
          title="טוקן MCP חדש"
          footer={
            <>
              <button type="button" className="btn" onClick={() => setCreateOpen(false)} disabled={saving}>ביטול</button>
              <button type="button" className="btn primary" onClick={() => void create()} disabled={saving}>
                {saving ? 'יוצר…' : 'צור טוקן'}
              </button>
            </>
          }
        >
          <div className="stack" style={{ gap: 12 }}>
            <FormField label="שם (למשל: המחשב של דורון)">
              <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="default" />
            </FormField>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-sm)', cursor: 'pointer' }}>
              <input type="checkbox" checked={readOnly} onChange={(e) => setReadOnly(e.target.checked)} />
              קריאה בלבד (חוסם כל כלי שמשנה נתונים)
            </label>
            <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 'var(--t-sm)' }}>
              תוקף: שנה. אפשר לבטל בכל רגע מהמסך הזה.
            </p>
          </div>
        </Modal>
      )}

      {created && (
        <Modal
          isOpen
          onClose={() => setCreated(null)}
          title="הטוקן נוצר — חברו את Claude"
          footer={<button type="button" className="btn primary" onClick={() => setCreated(null)}>סיום</button>}
        >
          <div className="stack" style={{ gap: 14 }}>
            <p style={{ margin: 0, color: 'var(--danger)', fontSize: 'var(--t-sm)' }}>
              ⚠️ זו הפעם היחידה שהטוקן מוצג. העתיקו את מה שצריך ושמרו אותו.
            </p>

            <strong style={{ fontSize: 'var(--t-sm)' }}>Claude Code</strong>
            <CopyBlock label="פקודת חיבור (הדביקו בטרמינל)" value={created.claude_code_command} />
            <CopyBlock label="לחלופין — entry ל-‎.mcp.json" value={JSON.stringify(created.mcp_json, null, 2)} />

            {created.claude_desktop_config && (
              <>
                <strong style={{ fontSize: 'var(--t-sm)' }}>Claude Desktop</strong>
                <CopyBlock
                  label="תצורה ל-claude_desktop_config.json"
                  value={JSON.stringify(created.claude_desktop_config, null, 2)}
                />
                {created.claude_desktop_config_path && (
                  <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', lineHeight: 1.7 }}>
                    מיקום הקובץ — macOS: <code dir="ltr">{created.claude_desktop_config_path.macos}</code>
                    <br />
                    Windows: <code dir="ltr">{created.claude_desktop_config_path.windows}</code>
                    <br />
                    אחרי השמירה יש לסגור ולפתוח מחדש את Claude Desktop.
                  </div>
                )}
              </>
            )}

            <CopyBlock label="הטוקן עצמו" value={created.token} />
          </div>
        </Modal>
      )}

      {revokeTarget && (
        <Modal
          isOpen
          onClose={() => setRevokeTarget(null)}
          title="ביטול טוקן"
          footer={
            <>
              <button type="button" className="btn" onClick={() => setRevokeTarget(null)}>ביטול</button>
              <button type="button" className="btn danger" onClick={() => void revoke(revokeTarget)}>בטל טוקן</button>
            </>
          }
        >
          <p style={{ margin: 0 }}>
            לבטל את הטוקן "{revokeTarget.name}"? חיבור Claude Code שמשתמש בו יפסיק לעבוד מיידית.
          </p>
        </Modal>
      )}
    </div>
  );
}
